import { Injectable } from '@angular/core';
import { ApiClientCredentialsService } from '@era/settings-sdk';
import { SlugifyPipe } from '@methylium/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { compare } from 'fast-json-patch';
import { mergeMap } from 'rxjs/operators';
import { safeMap } from '../effects.helpers';
import {
  ApiClientSettingApiActions,
  ApiClientSettingResolverActions,
  ApiClientSettingViewActions,
} from './api-client-setting.actions';

@Injectable()
export class ApiClientSettingEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly api: ApiClientCredentialsService
  ) {}

  loadSettingsEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApiClientSettingResolverActions.settingRequested),
      mergeMap(() =>
        safeMap(
          this.api.getApiClientCredentials(),
          (settings) => ApiClientSettingApiActions.settingsLoaded({ settings }),
          (error) => ApiClientSettingApiActions.settingsLoadedError({ error })
        )
      )
    );
  });

  updateSettingEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApiClientSettingViewActions.settingUpdated),
      mergeMap(({ originalSetting, setting }) => {
        const patch = compare(originalSetting.credentials, setting);
        return safeMap(
          this.api.changeCredentials(originalSetting.id, patch),
          () =>
            ApiClientSettingApiActions.settingUpdated({
              setting: {
                displayName: originalSetting.displayName,
                humanId: originalSetting.humanId,
                id: originalSetting.id,
                credentials: setting,
              },
            }),
          (error) => ApiClientSettingApiActions.settingUpdatedError({ setting: originalSetting, error })
        );
      })
    );
  });

  deleteSettingEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApiClientSettingViewActions.settingDeleted),
      mergeMap(({ setting }) =>
        safeMap(
          this.api.deleteApiClientCredential(setting.id),
          () => ApiClientSettingApiActions.settingDeleted({ setting }),
          (error) => ApiClientSettingApiActions.settingDeletedError({ setting, error })
        )
      )
    );
  });

  addSettingEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApiClientSettingViewActions.settingAdded),
      mergeMap(({ setting }) =>
        safeMap(
          this.api.addApiClientCredential({
            authorityUrl: setting.credentials.authorityUrl,
            clientId: setting.credentials.clientId,
            clientSecret: setting.credentials.clientSecret,
            displayName: setting.displayName,
            humanId:
              setting.humanId || new SlugifyPipe().transform(setting.displayName || setting.credentials.clientId),
            scope: setting.credentials.scope,
          }),
          (addedSetting) => ApiClientSettingApiActions.settingAdded({ setting: addedSetting }),
          (error) => ApiClientSettingApiActions.settingAddedError({ setting, error })
        )
      )
    );
  });
}
